import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from 'react-query';
import { requestsAPI } from '../../services/api';
import { FiArrowLeft, FiCheck, FiTruck, FiBox, FiMapPin, FiPackage, FiDollarSign } from 'react-icons/fi';
import { LuCheck } from 'react-icons/lu';
import LoadingSpinner from '../../components/LoadingSpinner';

const ShippingReview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Get data from navigation state
  const { shipmentData, selectedRate } = location.state || {};
  
  // Create request mutation
  const createRequestMutation = useMutation(
    (requestData) => requestsAPI.createRequest(requestData),
    {
      onSuccess: (data) => {
        navigate(`/customer/requests/${data.id}`, {
          state: { requestCreated: true }
        });
      },
      onError: (error) => {
        console.error('Failed to create shipping request:', error);
        // Handle error - could show toast notification
      }
    }
  );
  
  const handleSubmitRequest = async () => {
    if (!shipmentData || !selectedRate) {
      console.error('Missing shipment data or selected rate');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const requestPayload = {
        requestType: 'ship',
        scheduledDate: new Date(),
        source_warehouse_id: shipmentData.source_warehouse_id,
        destination_address_id: shipmentData.destination_address_id,
        shipping_method: shipmentData.shippingMethod,
        estimated_weight_lbs: shipmentData.weight,
        estimated_volume_cuft: shipmentData.volume,
        liftgate_required: shipmentData.liftgate_required || false,
        white_glove: shipmentData.white_glove || false,
        inside_delivery: shipmentData.inside_delivery || false,
        lineItems: shipmentData.lineItems,
        selectedCarrier: selectedRate.carrier,
        selectedService: selectedRate.service,
        estimatedCost: selectedRate.cost,
        specialInstructions: `Shipping via ${selectedRate.carrier} ${selectedRate.service}`
      };
      
      await createRequestMutation.mutateAsync(requestPayload);
    } catch (error) {
      console.error('Error submitting request:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
  
  if (!shipmentData || !selectedRate) {
    return (
      <div className="py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-gray-500">No shipping data found. Please start over.</p>
            <button
              onClick={() => navigate('/customer/requests/new?type=ship')}
              className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Create New Shipping Request
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="py-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Progress Steps */}
        <nav aria-label="Progress" className="mb-8">
          <ol className="flex items-center">
            <li className="relative pr-8 sm:pr-20 text-indigo-600">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="h-0.5 w-full bg-indigo-600"></div>
              </div>
              <div className="relative flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-white">
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="hidden sm:block absolute top-0 left-1/2 -translate-x-1/2 -translate-y-10 text-sm font-medium text-gray-500">
                Select Company & Request
              </div>
            </li>
            
            <li className="relative pr-8 sm:pr-20 text-indigo-600">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="h-0.5 w-full bg-indigo-600"></div>
              </div>
              <div className="relative flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-white">
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="hidden sm:block absolute top-0 left-1/2 -translate-x-1/2 -translate-y-10 text-sm font-medium text-gray-500">
                Details
              </div>
            </li>
            
            <li className="relative pr-8 sm:pr-20 text-indigo-600">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="h-0.5 w-full bg-indigo-600"></div>
              </div>
              <div className="relative flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-white">
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="hidden sm:block absolute top-0 left-1/2 -translate-x-1/2 -translate-y-10 text-sm font-medium text-gray-500">
                Shipping
              </div>
            </li>
            
            <li className="relative text-indigo-600">
              <div className="relative flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-white">
                <span>4</span>
              </div>
              <div className="hidden sm:block absolute top-0 left-1/2 -translate-x-1/2 -translate-y-10 text-sm font-medium text-indigo-600">
                Review
              </div>
            </li>
          </ol>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-indigo-600 hover:text-indigo-800 mb-4"
          >
            <FiArrowLeft className="mr-2" />
            Back to Shipping Rates
          </button>
          
          <h1 className="text-2xl font-semibold text-gray-900">Review & Confirm Shipment</h1>
          <p className="mt-2 text-sm text-gray-700">
            Please review all details before submitting your shipping request
          </p>
        </div>
        
        {/* Selected Shipping Service */}
        <div className="bg-white border rounded-lg p-6 mb-6">
          <div className="flex items-center mb-4">
            <div className="bg-green-100 rounded-full p-2 mr-3">
              <FiCheck className="h-5 w-5 text-green-600" />
            </div>
            <h2 className="text-lg font-medium text-gray-900">Selected Shipping Service</h2>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {shipmentData.shippingMethod === 'courier' ? (
                <FiBox className="text-blue-500 mr-3 h-8 w-8" />
              ) : (
                <FiTruck className="text-green-500 mr-3 h-8 w-8" />
              )}
              <div>
                <div className="text-lg font-medium text-gray-900">
                  {selectedRate.carrier} {selectedRate.service}
                </div>
                <div className="text-sm text-gray-500">
                  {selectedRate.transit_days} business days
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">
                {formatCurrency(selectedRate.cost)}
              </div>
              <div className="text-sm text-gray-500">Estimated cost</div>
            </div>
          </div>
        </div>
        
        {/* Shipping Details Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Source Information */}
          <div className="bg-white border rounded-lg p-6">
            <div className="flex items-center mb-4">
              <FiMapPin className="text-blue-500 mr-2 h-5 w-5" />
              <h3 className="font-medium text-gray-900">Ship From</h3>
            </div>
            <div className="text-sm text-gray-600">
              <p>{shipmentData.sourceAddress?.name || 'Source Warehouse'}</p>
              <p>{shipmentData.sourceAddress?.street || '123 Warehouse St'}</p>
              <p>
                {shipmentData.sourceAddress?.city || 'Calgary'}, {shipmentData.sourceAddress?.state || 'AB'} {shipmentData.sourceAddress?.postalCode || 'T2P 1J9'}
              </p>
              <p>{shipmentData.sourceAddress?.country || 'Canada'}</p>
            </div>
          </div>
          
          {/* Destination Information */}
          <div className="bg-white border rounded-lg p-6">
            <div className="flex items-center mb-4">
              <FiMapPin className="text-green-500 mr-2 h-5 w-5" />
              <h3 className="font-medium text-gray-900">Ship To</h3>
            </div>
            <div className="text-sm text-gray-600">
              <p>{shipmentData.destinationAddress?.name || 'Destination Address'}</p>
              <p>{shipmentData.destinationAddress?.street || '456 Customer Ave'}</p>
              <p>
                {shipmentData.destinationAddress?.city || 'Vancouver'}, {shipmentData.destinationAddress?.state || 'BC'} {shipmentData.destinationAddress?.postalCode || 'V6B 1A1'}
              </p>
              <p>{shipmentData.destinationAddress?.country || 'Canada'}</p>
            </div>
          </div>
        </div>
        
        {/* Shipment Items */}
        <div className="bg-white border rounded-lg p-6 mb-6">
          <div className="flex items-center mb-4">
            <FiPackage className="text-purple-500 mr-2 h-5 w-5" />
            <h3 className="font-medium text-gray-900">Items to Ship</h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Weight
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Volume
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Identifiers
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {shipmentData.lineItems?.map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{item.productSku}</div>
                        <div className="text-sm text-gray-500">{item.productName}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.quantity} {item.unitOfMeasure}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {(item.weight * item.quantity).toFixed(1)} lbs
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {(item.volume * item.quantity).toFixed(1)} cu ft
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.serialNumbers?.length > 0 || item.macAddresses?.length > 0 ? (
                        <span className="inline-flex items-center text-green-600">
                          <LuCheck className="h-4 w-4 mr-1" />
                          Assigned
                        </span>
                      ) : (
                        <span className="text-gray-400">Not required</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Totals */}
          <div className="mt-4 pt-4 border-t">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="font-medium">Total Items:</span> {shipmentData.lineItems?.reduce((sum, item) => sum + item.quantity, 0) || 0}
              </div>
              <div>
                <span className="font-medium">Total Weight:</span> {shipmentData.weight?.toFixed(1) || 0} lbs
              </div>
              <div>
                <span className="font-medium">Total Volume:</span> {shipmentData.volume?.toFixed(1) || 0} cu ft
              </div>
            </div>
          </div>
        </div>
        
        {/* Special Services */}
        {shipmentData.shippingMethod === 'freight' && (
          <div className="bg-white border rounded-lg p-6 mb-6">
            <h3 className="font-medium text-gray-900 mb-4">Special Services</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center">
                <span className={`w-2 h-2 rounded-full mr-2 ${shipmentData.liftgate_required ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                Liftgate Service {shipmentData.liftgate_required ? '(Included)' : '(Not requested)'}
              </div>
              <div className="flex items-center">
                <span className={`w-2 h-2 rounded-full mr-2 ${shipmentData.white_glove ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                White Glove Service {shipmentData.white_glove ? '(Included)' : '(Not requested)'}
              </div>
              <div className="flex items-center">
                <span className={`w-2 h-2 rounded-full mr-2 ${shipmentData.inside_delivery ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                Inside Delivery {shipmentData.inside_delivery ? '(Included)' : '(Not requested)'}
              </div>
            </div>
          </div>
        )}
        
        {/* Cost Breakdown */}
        <div className="bg-white border rounded-lg p-6 mb-6">
          <div className="flex items-center mb-4">
            <FiDollarSign className="text-green-500 mr-2 h-5 w-5" />
            <h3 className="font-medium text-gray-900">Cost Breakdown</h3>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Base Shipping ({selectedRate.carrier} {selectedRate.service})</span>
              <span>{formatCurrency(selectedRate.cost)}</span>
            </div>
            
            {shipmentData.liftgate_required && (
              <div className="flex justify-between text-sm">
                <span>Liftgate Service</span>
                <span>{formatCurrency(75)}</span>
              </div>
            )}
            
            {shipmentData.white_glove && (
              <div className="flex justify-between text-sm">
                <span>White Glove Service</span>
                <span>{formatCurrency(200)}</span>
              </div>
            )}
            
            {shipmentData.inside_delivery && (
              <div className="flex justify-between text-sm">
                <span>Inside Delivery</span>
                <span>{formatCurrency(125)}</span>
              </div>
            )}
            
            <hr className="my-2" />
            
            <div className="flex justify-between font-medium">
              <span>Estimated Total</span>
              <span>
                {formatCurrency(
                  selectedRate.cost +
                  (shipmentData.liftgate_required ? 75 : 0) +
                  (shipmentData.white_glove ? 200 : 0) +
                  (shipmentData.inside_delivery ? 125 : 0)
                )}
              </span>
            </div>
            
            <p className="text-xs text-gray-500 mt-2">
              * Final costs may vary based on actual weight, dimensions, and carrier surcharges
            </p>
          </div>
        </div>
        
        {/* Terms and Conditions */}
        <div className="bg-gray-50 border rounded-lg p-4 mb-6">
          <label className="flex items-start">
            <input type="checkbox" className="mt-1 mr-3" required />
            <span className="text-sm text-gray-700">
              I acknowledge that I have reviewed all shipping details and understand that once submitted, 
              this request will be processed by our fulfillment team. Changes may not be possible once 
              processing begins. I agree to the shipping terms and estimated costs shown above.
            </span>
          </label>
        </div>
        
        {/* Action Buttons */}
        <div className="flex justify-between">
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            disabled={isSubmitting}
          >
            Back to Rates
          </button>
          
          <button
            onClick={handleSubmitRequest}
            disabled={isSubmitting}
            className={`px-8 py-2 rounded-md ${
              isSubmitting
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {isSubmitting ? (
              <div className="flex items-center">
                <LoadingSpinner />
                <span className="ml-2">Submitting...</span>
              </div>
            ) : (
              'Confirm & Submit Request'
            )}
          </button>
        </div>
        
        {/* Help Text */}
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">Next Steps</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Your request will be reviewed by our fulfillment team</li>
            <li>• We'll confirm inventory availability and pick the items</li>
            <li>• Items will be packed and prepared for shipment</li>
            <li>• You'll receive tracking information once shipped</li>
            <li>• Estimated delivery: {selectedRate.transit_days} business days</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ShippingReview; 
